/**
 * Website store CTAs.
 *
 * Create React App inlines REACT_APP_* at build time, so changing these on
 * Vercel takes effect on the next deploy of that environment. That redeploy
 * does not require a Git commit.
 *
 * Canonical names:
 *   REACT_APP_IOS_APP_STORE_URL
 *   REACT_APP_ANDROID_PLAY_STORE_URL
 *
 * Older names still used in this repo are accepted so an existing Vercel
 * value is not ignored:
 *   REACT_APP_IOS_STORE_URL, REACT_APP_APP_STORE_URL
 *   REACT_APP_ANDROID_STORE_URL, REACT_APP_PLAY_STORE_URL
 *
 * A value counts as configured only when it is an https listing on the
 * official store host for that platform:
 *   iOS:     https://apps.apple.com/<path>
 *   Android: https://play.google.com/<path>
 *
 * No other host is accepted, including app.lookbel.com and arbitrary https
 * URLs. Anything else leaves the CTA disabled as Coming Soon.
 */

const IOS_STORE_HOST = 'apps.apple.com';
const ANDROID_STORE_HOST = 'play.google.com';

function readEnv(name: string): string | undefined {
  return process.env[name];
}

function readStoreUrl(value: string | undefined, host: string): string | null {
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  if (!raw || raw === '#' || /^javascript:/i.test(raw)) return null;

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }

  if (url.protocol !== 'https:') return null;
  if (url.username || url.password) return null;
  if (url.hostname !== host) return null;
  if (!url.pathname || url.pathname === '/') return null;
  return url.href;
}

function firstStoreUrl(names: string[], host: string): string | null {
  for (const name of names) {
    const url = readStoreUrl(readEnv(name), host);
    if (url) return url;
  }
  return null;
}

export const appLinks = {
  get ios(): string | null {
    return firstStoreUrl(
      [
        'REACT_APP_IOS_APP_STORE_URL',
        'REACT_APP_IOS_STORE_URL',
        'REACT_APP_APP_STORE_URL',
      ],
      IOS_STORE_HOST
    );
  },
  get android(): string | null {
    return firstStoreUrl(
      [
        'REACT_APP_ANDROID_PLAY_STORE_URL',
        'REACT_APP_ANDROID_STORE_URL',
        'REACT_APP_PLAY_STORE_URL',
      ],
      ANDROID_STORE_HOST
    );
  },
};

export function getAppLinks(): { ios: string | null; android: string | null } {
  return {
    ios: appLinks.ios,
    android: appLinks.android,
  };
}
